import React, { useContext } from 'react';
import Accordion from '../../common/Accordion';
import Checkbox from '../../common/Checkbox';
import Flex, { FlexDirection, FlexJustification } from '../../common/Flex';
import MultilineTooltip from '../../common/MultilineTooltip';
import Select from '../../common/Select';
import SliderInput from '../../common/SliderInput';
import TextInput from '../../common/TextInput';
import { useState } from 'react';
import { useHook } from '../../../hooks';
import { HomeContext } from '../Home';
import { useAppDispatch, useAppSelector, useTranslate } from '../../../hooks/common';
import { LoadType, StartingCondition, StartingType, ToleranceType } from '../../../enums';
import { INT_REGEX, STARTING_CONSTRAINTS, TRANSLATIONS } from '../../../constants';
import { fixNumberZeroFormat, getNumberOfStartupsMax, getStartingVoltageByType, mapToSliderInputText } from '../../../services';
import { DEFAULT_AC, DEFAULT_AW } from '../../../constants';
import { setAcText, setAwText, setMesRequest, setStartingVoltageText, setTags } from '../../../store';
import { showStatusMessage } from '@innomotics/innomotics-build-react-lib';

interface StartingConstraintProps {
    disabled: boolean;
}

const StartingConstraint = ({ disabled }: StartingConstraintProps) => {
    const translate = useTranslate();
    const dispatch = useAppDispatch();
    const calculationResponse = useAppSelector(state => state.motor.calculationResponse);
    const mesRequest = useAppSelector(state => state.motor.mesRequest);
    const startingVoltageText = useAppSelector(state => state.startingConstraint.startingVoltageText);
    const acText = useAppSelector(state => state.startingConstraint.acText);
    const awText = useAppSelector(state => state.startingConstraint.awText);
    const [accordionOpen, toggleAccordion] = useState(true);
    const { getMainBoundary } = useContext(HomeContext);
    const { startingVoltageRange, onStartingConstraintChange } = useHook(x => x.startingConstraint)();

    const isStartingTypeDol = mesRequest.commonData.startingType === StartingType.DOL;

    const handleStartingVoltageChange = (voltage: string) => {
        if (INT_REGEX.test(voltage)) {
            dispatch(setStartingVoltageText({ inputValue: voltage }));
        }
    };

    const handleStartingVoltageBlur = async (voltage: number) => {
        const { min, max } = startingVoltageRange;
        const lessThanMin = voltage < min;
        let correctValue: number = voltage;

        if (lessThanMin || voltage > max) {
            correctValue = lessThanMin ? min : max;

            dispatch(setStartingVoltageText(mapToSliderInputText(correctValue)));
        } else {
            dispatch(setStartingVoltageText({ sliderValue: correctValue }));
        }

        dispatch(setTags({ startingVoltage: correctValue }));

        await onStartingConstraintChange({ startingVoltage: correctValue });
    };

    const handleStartingTypeChange = async (value: { [key in StartingType]?: string }) => {
        const newStartingType = Number(Object.keys(value));
        const newStartingVoltage = getStartingVoltageByType(newStartingType, mesRequest.loadData.loadType);

        dispatch(setStartingVoltageText(mapToSliderInputText(newStartingVoltage)));
        dispatch(setTags({ startingVoltage: undefined, startingType: newStartingType }));

        await onStartingConstraintChange({ startingVoltage: newStartingVoltage, startingType: newStartingType });
    };

    const handleToleranceSelectChange = async (value: { [key in ToleranceType]?: string }) => {
        const newTolerance = Number(Object.keys(value)) !== ToleranceType.Without;

        dispatch(setTags({ isBreakdownToleranceOn: newTolerance }));

        await onStartingConstraintChange({ isBreakdownToleranceOn: newTolerance, isLockedRotorPullupToleranceOn: newTolerance }, true);
    };

    const handleStartupNumbersChange = async (value: boolean) => {
        if (value) {
            dispatch(setTags({ ac: undefined, aw: undefined }));

            await onStartingConstraintChange({ isStartupNumbersChecked: value, ac: DEFAULT_AC, aw: DEFAULT_AW });
        } else {
            await onStartingConstraintChange({ isStartupNumbersChecked: value });
        }
    };

    const handleNumberOfStartupsInputBlur = async (property: string, value: string) => {
        const numberOfStartupsNumber = Number(value);
        const numberOfStartupsMax = calculationResponse && getNumberOfStartupsMax(calculationResponse, property);

        if (!value) {
            dispatch(setMesRequest({ ...mesRequest, commonData: { ...mesRequest.commonData, [property]: value } }));
            return;
        }

        if (numberOfStartupsMax && numberOfStartupsNumber > numberOfStartupsMax) {
            const isAc = property === STARTING_CONSTRAINTS.coldCondition;

            isAc ? dispatch(setAcText(numberOfStartupsMax.toString())) : dispatch(setAwText(numberOfStartupsMax.toString()));
            dispatch(setTags({ [property]: numberOfStartupsMax }));

            await onStartingConstraintChange({ [property]: numberOfStartupsMax });

            showStatusMessage({
                message: isAc ? translate(TRANSLATIONS.snackbar.info.numberOfStartupsColdEnteredValueTooHigh.text) : translate(TRANSLATIONS.snackbar.info.numberOfStartupsWarmEnteredValueTooHigh.text),
                showProgress: true,
                type: 'info'
            });
        } else {
            dispatch(setTags({ [property]: value }));

            await onStartingConstraintChange({ [property]: numberOfStartupsNumber });
        }
    };

    const handleStartingConditionSelectChange = async (value: { [key in StartingCondition]?: string }) => {
        const startingCondition = Number(Object.keys(value));

        dispatch(setTags({ startingCondition }));

        await onStartingConstraintChange({ startingCondition });
    };

    const handleStartingVoltageSliderBlur = async (value: number) => {
        dispatch(setTags({ startingVoltage: value }));

        await onStartingConstraintChange({ startingVoltage: value });
    };

    const renderStartingTypes = () => {
        const { loadType } = mesRequest.loadData;
        const { startingType, ydSwitchPossible } = mesRequest.commonData;
        const startingTypes: { [key in StartingType]?: string }[] = [
            { '0': translate(TRANSLATIONS.filters.operationalData.startingConstraints.startingType.DOL) }
        ];

        if (ydSwitchPossible && loadType !== LoadType.Custom) {
            startingTypes.push({ '1': translate(TRANSLATIONS.filters.operationalData.startingConstraints.startingType.YD) });
        }

        return (
            <div className='select-with-tooltip'>
                <Select
                    disabled={disabled}
                    label={translate(TRANSLATIONS.filters.operationalData.startingConstraints.startingType.title)}
                    value={startingTypes[startingType]}
                    values={startingTypes}
                    mapToString={x => Object.values(x).toString()}
                    onSelect={handleStartingTypeChange}
                />
                <MultilineTooltip idName='startingTypeInfo' text={translate(TRANSLATIONS.tooltips.startingType)} html>
                    <></>
                </MultilineTooltip>
            </div>
        );
    };

    const renderTolerance = () => {
        const { isBreakdownToleranceOn, isLockedRotorPullupToleranceOn } = mesRequest.commonData;
        const startingConstraints: { [key in ToleranceType]?: string }[] = [
            { '0': translate(TRANSLATIONS.filters.operationalData.startingConstraints.tolerance.withTolerance) },
            { '1': translate(TRANSLATIONS.filters.operationalData.startingConstraints.tolerance.withoutTolerance) }
        ];

        return (
            <div className='select-with-tooltip'>
                <Select
                    disabled={disabled}
                    label={translate(TRANSLATIONS.filters.operationalData.startingConstraints.tolerance.title)}
                    value={startingConstraints[isBreakdownToleranceOn && isLockedRotorPullupToleranceOn ? '0' : '1']}
                    values={startingConstraints}
                    mapToString={x => Object.values(x).toString()}
                    onSelect={handleToleranceSelectChange}
                />
                <MultilineTooltip idName='toleranceInfo' text={translate(TRANSLATIONS.tooltips.tolerance)} html>
                    <> </>
                </MultilineTooltip>
            </div>

        );
    };

    const renderStartingConditions = () => {
        const { startingCondition } = mesRequest.commonData;
        const startingConditions: { [key in StartingCondition]?: string }[] = [
            { '0': translate(TRANSLATIONS.filters.operationalData.startingConstraints.startingCondition.Cold) },
            { '1': translate(TRANSLATIONS.filters.operationalData.startingConstraints.startingCondition.Warm) }
        ];

        return (
            <Select
                disabled={disabled}
                label={translate(TRANSLATIONS.filters.operationalData.startingConstraints.startingCondition.title)}
                value={startingConditions[startingCondition]}
                values={startingConditions}
                mapToString={x => Object.values(x).toString()}
                onSelect={handleStartingConditionSelectChange}
            />
        );
    };

    const renderStartupNumbers = () => {
        const numberOfStartupsColdMax = calculationResponse && calculationResponse.loadOperationValues && calculationResponse.loadOperationValues.maxNumberOfACold;
        const numberOfStartupsWarmMax = calculationResponse && calculationResponse.loadOperationValues && calculationResponse.loadOperationValues.maxNumberOfAWarm;
        const { isStartupNumbersChecked } = mesRequest.commonData;

        return (
            <>
                <div className='label-input-with-tooltip'>
                    <div className='number-of-startups'>
                        <div className='number-of-startups-label'>{translate(TRANSLATIONS.filters.operationalData.startingConstraints.numberOfStartUps.title)}</div>
                        <Checkbox
                            disabled={disabled}
                            checked={isStartupNumbersChecked}
                            label={translate(TRANSLATIONS.filters.operationalData.startingConstraints.numberOfStartUps.text)}
                            onChange={handleStartupNumbersChange}
                        />
                    </div>
                    <MultilineTooltip idName='numberOfStartUpsInfo' text={translate(TRANSLATIONS.tooltips.numberOfStartUps)} html>
                        <></>
                    </MultilineTooltip>
                </div>                
                <TextInput
                    disabled={disabled || isStartupNumbersChecked}
                    label={translate(TRANSLATIONS.filters.operationalData.startingConstraints.ac)}
                    value={acText ? acText.toString() : ''}
                    name={STARTING_CONSTRAINTS.coldCondition}
                    onChange={value => INT_REGEX.test(value) && dispatch(setAcText(fixNumberZeroFormat(value)))}
                    onBlur={e => handleNumberOfStartupsInputBlur(STARTING_CONSTRAINTS.coldCondition, e.target.value)}
                    onFocusText={numberOfStartupsColdMax ? `0 \u2264 ... \u2264 ${numberOfStartupsColdMax}` : undefined}
                />
                <TextInput
                    disabled={disabled || isStartupNumbersChecked}
                    label={translate(TRANSLATIONS.filters.operationalData.startingConstraints.aw)}
                    value={awText ? awText.toString() : ''}
                    name={STARTING_CONSTRAINTS.warmCondition}
                    onChange={value => INT_REGEX.test(value) && dispatch(setAwText(fixNumberZeroFormat(value)))}
                    onBlur={e => handleNumberOfStartupsInputBlur(STARTING_CONSTRAINTS.warmCondition, e.target.value)}
                    onFocusText={numberOfStartupsWarmMax ? `0 \u2264 ... \u2264 ${numberOfStartupsWarmMax}` : undefined}
                />
            </>
        );
    };

    return (
        <div className='starting-constraint-container'>
            <Accordion
                iconFont={'e940'}
                label={translate(TRANSLATIONS.filters.operationalData.startingConstraints.title)}
                open={accordionOpen}
                onClick={() => toggleAccordion(!accordionOpen)}
            >
                <Flex direction={FlexDirection.Column} justification={FlexJustification.Center} gap={15}>
                    <div className='label-input-with-tooltip'>
                        <SliderInput
                            name='startingVoltage'
                            value={startingVoltageText}
                            range={startingVoltageRange}
                            label={translate(TRANSLATIONS.filters.operationalData.startingConstraints.startingVoltage)}
                            unit='%'
                            disabled={disabled || mesRequest.loadData.loadType === LoadType.Custom || !isStartingTypeDol}
                            onInputChange={handleStartingVoltageChange}
                            onInputBlur={value => handleStartingVoltageBlur(value)}
                            onSliderChange={value => dispatch(setStartingVoltageText(mapToSliderInputText(value)))}
                            onSliderBlur={value => handleStartingVoltageSliderBlur(value)}
                        />
                        <MultilineTooltip idName='startingVoltageInfo' text={translate(TRANSLATIONS.tooltips.startingVoltage)} html>
                            <></>
                        </MultilineTooltip>
                    </div>
                    {renderStartingTypes()}
                    {renderTolerance()}
                    {renderStartupNumbers()}
                    {renderStartingConditions()}
                </Flex>
            </Accordion>
        </div>
    );
};

export default StartingConstraint;
